import { useState, useEffect } from "react";
import { getBankLedger } from "@/lib/api";
import { logWarn } from "@/lib/log";
import { Loader2 } from "lucide-react";

const LEDGER_REFRESH_MS = 15000;

const KIND_STYLES = {
    topup: "text-green-400 border-green-400/40",
    withdraw: "text-blaze border-blaze/40",
    grant: "text-electric border-electric/40",
    usage: "text-white/50 border-white/20",
};

const LedgerRow = ({ e }) => (
    <div
        className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-white/5 font-mono text-[11px] items-center"
        data-testid={`bank-ledger-row-${e.entry_id}`}
    >
        <div className="col-span-3 text-white/40">{new Date(e.created_at).toLocaleString()}</div>
        <div className="col-span-2">
            <span className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] ${KIND_STYLES[e.kind] || KIND_STYLES.usage}`}>
                {e.kind}
            </span>
        </div>
        <div className="col-span-3 text-white/60 truncate">{e.email || "—"}</div>
        <div className="col-span-2 text-white/40 truncate">{e.note}</div>
        <div className={`col-span-2 text-right font-bold ${e.amount >= 0 ? "text-green-400" : "text-blaze"}`}>
            {e.amount >= 0 ? `+${e.amount.toLocaleString()}` : e.amount.toLocaleString()}
        </div>
    </div>
);

export const BankLedger = () => {
    const [entries, setEntries] = useState(null);

    useEffect(() => {
        const load = () =>
            getBankLedger()
                .then((d) => setEntries(d.entries))
                .catch((e) => logWarn("bank ledger load failed", e));
        load();
        const timer = setInterval(load, LEDGER_REFRESH_MS);
        return () => clearInterval(timer);
    }, []);

    if (entries === null) {
        return (
            <div className="p-6 flex justify-center">
                <Loader2 className="w-4 h-4 text-blaze animate-spin" />
            </div>
        );
    }
    if (entries.length === 0) {
        return (
            <div className="p-6 font-mono text-[11px] text-white/30 text-center" data-testid="bank-ledger-empty">
                No movements yet — top-ups, grants and streaming usage will appear here.
            </div>
        );
    }
    return (
        <div className="max-h-72 overflow-y-auto" data-testid="bank-ledger">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-white/30 border-b border-white/10 sticky top-0 bg-surface">
                <div className="col-span-3">When</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-3">Who</div>
                <div className="col-span-2">Note</div>
                <div className="col-span-2 text-right">Credits</div>
            </div>
            {entries.map((e) => (
                <LedgerRow key={e.entry_id} e={e} />
            ))}
        </div>
    );
};
